import React from "react";

import { Button, useToast } from "@/components";
import { useI18n } from "@/context/i18n";

declare global {
  interface Window {
    FB: FacebookSDK;
    fbAsyncInit?: () => void;
  }
}

interface IFacebookLoginProps {
  appClientId: string;
  onSuccess: (credentialsResponse: { userId: string; token: string }) => void;
}

export const FacebookLogin: React.FC<IFacebookLoginProps> = ({
  appClientId,
  onSuccess,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();

  React.useEffect(() => {
    const loadFacebookSDK = () => {
      return new Promise<void>((resolve) => {
        if (window.FB) {
          resolve();
          return;
        }

        window.fbAsyncInit = () => {
          window.FB.init({
            appId: appClientId,
            cookie: true,
            xfbml: true,
            version: "v15.0",
          });
          resolve();
        };

        const script = document.createElement("script");
        script.src = "https://connect.facebook.net/en_US/sdk.js";
        script.async = true;
        script.defer = true;
        script.onload = () => console.log("Facebook SDK script loaded");
        script.onerror = () =>
          console.error("Error loading Facebook SDK script");
        document.body.appendChild(script);
      });
    };

    loadFacebookSDK();
  }, [appClientId]);

  const facebookLogin = () => {
    if (window.FB) {
      window.FB.login(
        (response: FacebookLoginResponse) => {
          if (response.authResponse) {
            const { accessToken, userID } = response.authResponse;
            return onSuccess({ userId: userID, token: accessToken });
          }
          return toast({
            title: t("auth.failedToLoginWithFacebook"),
            description: "",
            variant: "destructive",
          });
        },
        { scope: "email,public_profile" }
      );
    }
  };

  return (
    <Button onClick={facebookLogin} variant="outline" className="w-full">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 mr-2 text-blue-600"
        viewBox="0 0 24 24"
      >
        <path
          fill="currentColor"
          d="M9.198 21.5h4v-8.01h3.604l.396-3.98h-4V7.5a1 1 0 0 1 1-1h3v-4h-3a5 5 0 0 0-5 5v2.01h-2l-.396 3.98h2.396v8.01Z"
        />
      </svg>
      {t("auth.facebookLogin")}
    </Button>
  );
};
